import { useState, useEffect, useRef } from 'preact/hooks';
import { t } from '../i18n';
import { navigateToView } from '../navigation';
import { resetPassword } from '../api';
import { store } from '../store';
import type { WidgetConfig } from '../config';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_RE = /^[a-zA-Z0-9]{10}$/;

function validateEmail(v: string): boolean {
  return EMAIL_RE.test(v.trim());
}
function validateCode(v: string): boolean {
  return v.trim().length === 10 && CODE_RE.test(v.trim());
}
function validatePassword(v: string): boolean {
  return v.length >= 5;
}

interface ResetPasswordViewProps {
  config: WidgetConfig;
}

export function ResetPasswordView({ config }: ResetPasswordViewProps) {
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const submissionPendingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const qEmail = params.get('email');
      const qCode = params.get('reset_code');
      if (qEmail) setEmail(qEmail);
      else {
        try {
          const stored = sessionStorage.getItem('sw_reset_email');
          if (stored) setEmail(stored);
        } catch {
          /* ignore */
        }
      }
      if (qCode) setResetCode(qCode);
    }
  }, []);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (submissionPendingRef.current) return;
    submissionPendingRef.current = true;
    let submissionSucceeded = false;
    try {
      setError('');
      const fe: Record<string, string> = {};
      if (!validateEmail(email)) fe.email = t('forgotPassword.invalidEmail');
      if (!validateCode(resetCode)) fe.resetCode = t('resetPassword.resetCodeFormat');
      if (!validatePassword(password)) fe.password = t('resetPassword.passwordMin');
      if (password !== confirm) fe.confirm = t('resetPassword.passwordMismatch');
      else if (!validatePassword(confirm)) fe.confirm = t('resetPassword.passwordMin');
      setFieldErrors(fe);
      if (Object.keys(fe).length) return;

      setLoading(true);
      const res = await resetPassword(config, email.trim(), resetCode.trim(), password);
      if (res.status === 200 && !res.error) {
        submissionSucceeded = true;
        setSuccess(true);
        store.addToast(t('resetPassword.success'), 'success');
        setTimeout(() => navigateToView('checkout-login'), 1500);
      } else {
        const messages = (res as any).messages as Record<string, string> | undefined;
        setError(messages?.E ?? res.error ?? t('api.genericError'));
      }
    } catch {
      setError(t('api.networkError'));
    } finally {
      submissionPendingRef.current = submissionSucceeded;
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div class="text-center py-4">
        <div class="alert alert-success">{t('resetPassword.success')}</div>
        <p class="text-muted small mb-0">{t('resetPassword.redirectHint')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate class="mw-400 mx-auto">
      <div class="mb-3">
        <label class="form-label">{t('resetPassword.emailLabel')}</label>
        <input
          type="email"
          class={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
          placeholder={t('forgotPassword.emailPlaceholder')}
          value={email}
          onInput={(e) => {
            setEmail((e.target as HTMLInputElement).value);
            setFieldErrors((f) => ({ ...f, email: '' }));
          }}
          disabled={loading}
        />
        {fieldErrors.email && <div class="small text-danger mt-1">{fieldErrors.email}</div>}
      </div>
      <div class="mb-3">
        <label class="form-label">{t('resetPassword.resetCodeLabel')}</label>
        <input
          type="text"
          class={`form-control ${fieldErrors.resetCode ? 'is-invalid' : ''}`}
          placeholder={t('resetPassword.resetCodePlaceholder')}
          maxLength={10}
          value={resetCode}
          onInput={(e) => {
            setResetCode((e.target as HTMLInputElement).value);
            setFieldErrors((f) => ({ ...f, resetCode: '' }));
          }}
          disabled={loading}
        />
        {fieldErrors.resetCode && <div class="small text-danger mt-1">{fieldErrors.resetCode}</div>}
      </div>
      <div class="mb-3">
        <label class="form-label">{t('resetPassword.newPasswordLabel')}</label>
        <input
          type="password"
          class={`form-control ${fieldErrors.password ? 'is-invalid' : ''}`}
          placeholder={t('resetPassword.passwordPlaceholder')}
          value={password}
          onInput={(e) => {
            setPassword((e.target as HTMLInputElement).value);
            setFieldErrors((f) => ({ ...f, password: '' }));
          }}
          disabled={loading}
        />
        {fieldErrors.password && <div class="small text-danger mt-1">{fieldErrors.password}</div>}
      </div>
      <div class="mb-3">
        <label class="form-label">{t('resetPassword.confirmLabel')}</label>
        <input
          type="password"
          class={`form-control ${fieldErrors.confirm ? 'is-invalid' : ''}`}
          placeholder={t('resetPassword.passwordPlaceholder')}
          value={confirm}
          onInput={(e) => {
            setConfirm((e.target as HTMLInputElement).value);
            setFieldErrors((f) => ({ ...f, confirm: '' }));
          }}
          disabled={loading}
        />
        {fieldErrors.confirm && <div class="small text-danger mt-1">{fieldErrors.confirm}</div>}
      </div>
      {error && <div class="alert alert-danger text-danger py-2 small mb-3">{error}</div>}
      <button type="submit" class="btn btn-primary w-100" disabled={loading}>
        {loading ? <span class="spinner-border spinner-border-sm me-2"></span> : null}
        {t('resetPassword.submit')}
      </button>
      <p class="mt-3 mb-0">
        <a href="#" onClick={(e) => { e.preventDefault(); navigateToView('checkout-login'); }} class="small">
          {t('forgotPassword.backToLogin')}
        </a>
      </p>
    </form>
  );
}
